// The small pieces every pipeline screen is built from (Iris's approved screens, docs/pipeline-screens.html): the step
// strip, a value with its underline (dashed typed, solid priced), the seat tag (gold You for the Owner seat), late and
// quiet words, the review link beside a step, a step's own controls by its done-rule, Hand to, Discuss, and the undo
// row a tap leaves behind.

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PipelineStep } from '@zerogap/engine';
import { toast, type Row } from '../../lib/api';
import { contactName, money, monthDay } from '../../lib/format';
import { handOff, lostRetouch, seatWords, setOutcome, tickStep, undoTap, type Lead, type Pipeline, type Tap } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { Btn, Tag } from '../../ui/kit';

/**
 * The step bar on a card (SM4 and SM8, 2026-10-24): one box per step the lead has (the engine's step list, not every step
 * of the stage), done filled, the one on now lit, a step whose condition is not settled yet dashed, to do empty. The
 * same bar, small, as the opportunity screen's.
 */
export function Strip({ lead, wide }: { lead: Lead; wide?: boolean }) {
  const pos = lead.position;
  const rows = lead.stepList.filter((r) => r.status !== 'skipped');
  if (!pos && rows.length === 0) return null;
  const at = rows.findIndex((r) => r.status === 'current') + 1;
  const total = rows.length || pos?.total || 0;
  return (
    <span className={`pstrip${wide ? ' wide' : ''}`} title={at ? `Step ${at} of ${total}` : 'Not placed on a step yet'}>
      <span className="pdots">
        {rows.length > 0 ? rows.map((r) => <i key={r.step.id} className={r.status} title={r.step.name} />) : pos?.strip.map((seg, i) => <i key={i} className={seg} />)}
      </span>
      <small className="num">{at ? `${at} of ${total}` : `0 of ${total}`}</small>
    </span>
  );
}

/** What a lead is worth, with the underline that says how it is known: dashed for a typed estimate, solid for a priced job. */
export function LeadValue({ lead, big }: { lead: Lead; big?: boolean }) {
  const { can } = useSession();
  const v = lead.value;
  if (!can('see_prices')) return <span className="muted">—</span>;
  const cls = big ? 'pv big-num' : 'pv';
  if (v.kind === 'priced')
    return (
      <span className={cls}>
        <span className="priced num">{money(v.cents, { whole: !big })}</span>
        {v.unverified > 0 && (
          <>
            {' '}
            <Tag kind="un">{v.unverified} unverified</Tag>
          </>
        )}
      </span>
    );
  if (v.kind === 'typed') return <span className={`${cls} typed num`}>~{money(v.cents, { whole: true })}</span>;
  if (v.kind === 'range') return <span className={`${cls} typed num`}>{money(v.low_cents, { whole: true })} to {money(v.high_cents, { whole: true })}</span>;
  return <small>No value</small>;
}

/** The likelihood, with "quiet n d" when the lead has gone quiet (its likelihood is halved then). */
export function Likelihood({ lead }: { lead: Lead }) {
  if (lead.likelihood === null) return <small>—</small>;
  return (
    <small className="num">
      {lead.likelihood}%{lead.quiet && lead.quietDays !== null && <span className="pquiet"> quiet {lead.quietDays} d</span>}
      {lead.quiet && lead.quietDays === null && <span className="pquiet"> never touched</span>}
    </small>
  );
}

/** Whose step it is: gold You for the Owner seat, a plain tag for everyone else. */
export function SeatTag({ p, step }: { p: Pipeline; step: PipelineStep | null | undefined }) {
  const w = seatWords(p, step);
  if (!w.words) return null;
  return w.you ? <span className="pyou">You</span> : <span className="pown">{w.words}</span>;
}

/** The next touch, amber when it has passed. */
export function NextTouch({ lead, prefix = 'Next touch' }: { lead: Lead; prefix?: string }) {
  if (!lead.nextTouch) return <small>{prefix} not set</small>;
  if (lead.nextTouchLate !== null && lead.nextTouchLate > 0)
    return (
      <span className="plate">
        {prefix} {monthDay(lead.nextTouch)} · {lead.nextTouchLate} d late
      </span>
    );
  return (
    <small>
      {prefix} {lead.nextTouch === new Date().toISOString().slice(0, 10) ? 'today' : monthDay(lead.nextTouch)}
    </small>
  );
}

/** The thing to review beside the step on now: a document icon, the link, who left it. */
export function ReviewLine({ p, lead, after, plain }: { p: Pipeline; lead: Lead; after?: ReactNode; plain?: boolean }) {
  const r = lead.reviewLink;
  if (!r) return null;
  const { seat, holder } = plain ? { seat: null, holder: null } : p.seatOf(r.step);
  return (
    <span className="prev">
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
        <path d="M3 1.5h6l4 4v9H3z" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <path d="M9 1.5v4h4" fill="none" stroke="currentColor" strokeWidth="1.4" />
      </svg>
      <a href={r.link} target="_blank" rel="noreferrer" className="plink">
        {r.step.name}
        {r.round > 1 ? `, round ${r.round}` : ''}
      </a>
      <small>
        {seat ? ` · ${seat.name}${holder ? ` · ${holder.name}` : ''}` : ''}
      </small>
      {after}
    </span>
  );
}

/** How long the lead has sat on its step, as a bar and a count. */
export function DaysOnStep({ lead }: { lead: Lead }) {
  if (lead.daysOnStep === null) return null;
  return (
    <span className="pdays" title="Days on this step">
      <i style={{ width: Math.min(96, Math.max(4, lead.daysOnStep * 4)) }} />
      {lead.daysOnStep} d
    </span>
  );
}

/** The undo row a tap leaves behind: what happened, Undo. */
export function UndoRow({ tap, onUndo }: { tap: Tap; onUndo: () => void }) {
  return (
    <>
      <div className="pundo">
        <span>{tap.words}</span>
        <button type="button" className="linklike plink" onClick={onUndo}>
          Undo
        </button>
      </div>
      <small>{tap.was}</small>
    </>
  );
}

/** The undo state a screen keeps: the last tap per lead. */
export function useTaps() {
  const [taps, setTaps] = useState<Record<string, Tap>>({});
  return {
    tap: (leadId: string) => taps[leadId] ?? null,
    remember: (leadId: string, tap: Tap) => setTaps((t) => ({ ...t, [leadId]: tap })),
    forget: (leadId: string) => setTaps((t) => {
      const { [leadId]: _, ...rest } = t;
      return rest;
    }),
    undo: async (leadId: string) => {
      const tap = taps[leadId];
      if (!tap) return;
      await undoTap(tap.entry);
      setTaps((t) => {
        const { [leadId]: _, ...rest } = t;
        return rest;
      });
    },
  };
}

/**
 * The step's own controls, by its done-rule: a step that records an answer offers the answer's choices (set and tick in one
 * tap); the seat ticks it: Done, or the step's own words ("Shared"), with a link box when the step leaves a review link; a
 * second one-tap answer ("Range already agreed") ticks it with its words on the log (Spencer, 2026-10-22; no skip); Owner
 * approves: Mark ready with a link for the seat, Approve for the Owner, and with a back-to step "Yes · another pass / No ·
 * move on"; an event: it happens on its own, with a Mark done override; the client acts: It landed. Park (needs the
 * re-approach date) and Lost (needs the reason) beside them when asked for, or always on a date step that ends in one.
 */
export function StepControls({ p, lead, onTap, withOutcome, compact }: { p: Pipeline; lead: Lead; onTap: (t: Tap) => void; withOutcome?: boolean; compact?: boolean }) {
  const s = useSession();
  const [link, setLink] = useState('');
  const [parking, setParking] = useState(false);
  const [losing, setLosing] = useState(false);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(lead.nextTouch ?? '');
  const step = lead.step;
  if (!s.canEdit('screen.pipeline') || !step || lead.outcome !== 'open') return null;
  const run = (f: () => Promise<Tap>) => () => void f().then(onTap, () => undefined);
  const outcomes = withOutcome || lead.dateStep;
  const lostWeeks = s.setting<{ weeks?: number }>('pipeline_lost_retouch', {}).weeks ?? null;
  const answerKey = step.sets_answer as string | null | undefined;
  const choices = answerKey === 'first_visit' ? p.answers.first_visit : answerKey === 'design_lane' ? p.answers.design_lane : null;
  const flag = answerKey && !choices ? p.answers.flags.find((f) => f.key === answerKey) ?? { key: answerKey, label: answerKey } : null;
  const backTo = step.back_to_step_id ? p.stepById(step.back_to_step_id) : null;
  const you = p.seatOf(step).seat?.id === p.ownerSeat;

  let main: ReactNode;
  if (choices) {
    main = choices.map((c) => (
      <Btn key={c.key} onClick={run(() => tickStep(p, lead, step, 'done', { answer: { [answerKey!]: c.key } as never }))}>
        {c.label}
      </Btn>
    ));
  } else if (flag) {
    main = (
      <>
        <Btn onClick={run(() => tickStep(p, lead, step, 'done', { answer: { flags: { [flag.key]: true } } }))}>Yes · {flag.label.toLowerCase()}</Btn>
        <Btn onClick={run(() => tickStep(p, lead, step, 'done', { answer: { flags: { [flag.key]: false } } }))}>No</Btn>
      </>
    );
  } else if (step.done_rule === 'owner_approves') {
    if (lead.readyRow || you) {
      main = backTo ? (
        <>
          <Btn onClick={run(() => tickStep(p, lead, step, 'done', { to: backTo, note: 'another pass' }))}>Yes · another pass</Btn>
          <Btn onClick={run(() => tickStep(p, lead, step, 'done'))}>No · move on</Btn>
        </>
      ) : (
        <Btn primary onClick={run(() => tickStep(p, lead, step, 'done'))}>
          Approve
        </Btn>
      );
    } else {
      main = (
        <>
          <input type="url" className="plinkbox" placeholder="Link to what is ready" value={link} onChange={(e) => setLink(e.target.value)} aria-label="Review link" />
          <Btn onClick={run(() => tickStep(p, lead, step, 'ready', { link: link.trim() || null }))}>Mark ready</Btn>
        </>
      );
    }
  } else if (step.done_rule === 'event') {
    main = (
      <>
        <Btn small onClick={run(() => tickStep(p, lead, step, 'done', { note: 'marked by hand' }))}>
          Mark done anyway
        </Btn>
      </>
    );
  } else if (step.done_rule === 'client_acts') {
    main = <Btn primary onClick={run(() => tickStep(p, lead, step, 'done'))}>It landed</Btn>;
  } else {
    main = (
      <>
        {step.review_link && <input type="url" className="plinkbox" placeholder="Link to the draft, optional" value={link} onChange={(e) => setLink(e.target.value)} aria-label="Review link" />}
        <Btn primary onClick={run(() => tickStep(p, lead, step, 'done', { link: link.trim() || null }))}>
          {step.tick_words || 'Done'}
        </Btn>
      </>
    );
  }
  // the second one-tap answer: the same tick, its words on the log as the reason (Share the Budget Range: Range already agreed)
  const second = step.second_answer ? <Btn onClick={run(() => tickStep(p, lead, step, 'done', { note: step.second_answer! }))}>{step.second_answer}</Btn> : null;

  return (
    <div className={`pctl${compact ? ' compact' : ''}`}>
      {main}
      {second}
      {outcomes && !parking && !losing && (
        <>
          <Btn onClick={() => setParking(true)}>Park</Btn>
          <Btn onClick={() => setLosing(true)}>Lost</Btn>
        </>
      )}
      {parking && (
        <span className="pinline">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Re-approach date" />
          <Btn primary disabled={!date} title={date ? '' : 'A park needs a next touch: the date to re-approach'} onClick={run(() => setOutcome(lead, 'parked', null, date).finally(() => setParking(false)))}>
            Park until then
          </Btn>
          <Btn read small onClick={() => setParking(false)}>
            Cancel
          </Btn>
        </span>
      )}
      {losing && (
        <span className="pinline">
          <input type="text" placeholder="The reason" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Lost reason" />
          <Btn primary disabled={!reason.trim()} title={reason.trim() ? '' : 'A loss needs a reason'} onClick={run(() => setOutcome(lead, 'lost', reason.trim(), lostRetouch(lostWeeks)).finally(() => setLosing(false)))}>
            Mark lost
          </Btn>
          <Btn read small onClick={() => setLosing(false)}>
            Cancel
          </Btn>
          {lostRetouch(lostWeeks) && <small>re-approach {monthDay(lostRetouch(lostWeeks))}</small>}
        </span>
      )}
    </div>
  );
}

/** Bring back: a parked or lost lead goes back to open where it was (the stage and the step it kept); nothing else to choose. */
export function BringBack({ lead, onTap, small }: { lead: Lead; onTap: (t: Tap) => void; small?: boolean }) {
  const s = useSession();
  if (!s.canEdit('screen.pipeline') || (lead.outcome !== 'parked' && lead.outcome !== 'lost' && lead.outcome !== 'not_a_lead')) return null;
  return (
    <Btn small={small} onClick={() => void setOutcome(lead, 'open').then(onTap, () => undefined)} title={lead.returnTo ? `Back to ${lead.returnTo.stage.name} · ${lead.returnTo.step.name}` : 'Back to open'}>
      Bring back
    </Btn>
  );
}

/**
 * A parked lead's row (Iris's panel 1, 2026-10-22): the name dimmed with the word Parked beside it, the re-approach date,
 * under it where it comes back to ("Returns to Intake · Capture the lead") or, with no step on record, that the Pipeline
 * manager sets one; Bring back at full weight. The row counts here and in no stage.
 */
export function ParkedRow({ lead, onTap }: { lead: Lead; onTap: (t: Tap) => void }) {
  return (
    <div className="pln parked">
      <span>
        <Link to={`/opportunities/${lead.id}`} className="rowlink pname">
          {lead.name}
        </Link>
        <span className="pword">
          Parked · {lead.nextTouch ? <span className={lead.nextTouchLate ? 'plate' : 'num'}>{monthDay(lead.nextTouch)}</span> : 'no date'}
        </span>
        <span className={`pret${lead.returnTo ? '' : ' gap'}`}>
          {lead.returnTo ? (
            <>
              Returns to <b>{lead.returnTo.stage.name} · {lead.returnTo.step.name}</b>
            </>
          ) : (
            'Return step: Pipeline manager to set'
          )}
        </span>
      </span>
      <BringBack lead={lead} onTap={onTap} small />
    </div>
  );
}

/** Hand the next move to someone: a task for the owner, or an AI employee; an optional note. */
export function HandTo({ p, lead, onDone }: { p: Pipeline; lead: Lead; onDone?: () => void }) {
  const s = useSession();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState<string>('me');
  const [note, setNote] = useState('');
  if (!s.canEdit('screen.pipeline')) return null;
  const people = p.users.filter((u) => u.active && u.is_ai && !u.is_script && u.id !== s.user?.id);
  const name = to === 'me' ? 'me' : people.find((u) => u.id === to)?.name ?? '';
  const send = async () => {
    await handOff(lead, lead.step, to === 'me' ? null : to, to === 'me', note.trim());
    toast(to === 'me' ? 'Noted as a task for you.' : `Handed to ${name}.`);
    setOpen(false);
    setNote('');
    onDone?.();
  };
  return (
    <>
      <Btn small={!open} primary={open} onClick={() => setOpen(!open)} title="Who is doing the next move on this lead">
        Hand to {open ? '▴' : '▾'}
      </Btn>
      {open && (
        <div className="phand">
          <span className="lb" style={{ marginBottom: 0 }}>
            Hand the next move to
          </span>
          <div className="pchips">
            <button type="button" className={`pchip${to === 'me' ? ' on' : ''}`} onClick={() => setTo('me')}>
              Me · a task
            </button>
            {people.map((u) => (
              <button key={u.id} type="button" className={`pchip${to === u.id ? ' on' : ''}`} onClick={() => setTo(u.id)}>
                {u.name}
              </button>
            ))}
          </div>
          <input type="text" placeholder="A note, optional" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note for the hand-off" />
          <Btn primary onClick={() => void send()}>
            Hand to {name}
          </Btn>
        </div>
      )}
    </>
  );
}

/** Discuss: a chat with an AI employee about this lead, opened through its link in the discuss_links setting, else the opening line copied. */
export function Discuss({ p, lead }: { p: Pipeline; lead: Lead }) {
  const s = useSession();
  const [open, setOpen] = useState(false);
  const links = s.setting<Record<string, string>>('discuss_links', {});
  const people = p.users.filter((u) => u.active && u.is_ai && !u.is_script);
  const go = (u: Row) => {
    const text = `Hi ${u.name}, about ${lead.name}${lead.step ? ` (on ${lead.step.name})` : ''}: `;
    const url = links?.[String(u.email ?? '').toLowerCase()];
    setOpen(false);
    if (url) window.open(url.replace('{text}', encodeURIComponent(text)), '_blank', 'noreferrer');
    else void navigator.clipboard?.writeText(text).then(() => toast(`Opening line copied. Paste it into ${u.name}'s chat.`), () => toast('Could not copy; no chat link is set for this employee (Settings → discuss_links).'));
  };
  return (
    <>
      <Btn small read onClick={() => setOpen(!open)} title="Open a chat with an employee about this lead">
        Discuss {open ? '▴' : '▾'}
      </Btn>
      {open && (
        <div className="pchips">
          {people.map((u) => (
            <button key={u.id} type="button" className="pchip read" onClick={() => go(u)}>
              {u.name}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** The lead's name as a link to its screen. */
export function LeadLink({ lead, children }: { lead: Lead; children?: ReactNode }) {
  return (
    <Link to={`/opportunities/${lead.id}`} className="rowlink">
      {children ?? lead.name}
    </Link>
  );
}

/** "Lauren Schoeberl primary · + contact" */
export function ContactLine({ lead }: { lead: Lead }) {
  return (
    <>
      {lead.contacts.map((c, i) => (
        <span key={c.id}>
          {i > 0 && ' · '}
          <Link to={`/people?contact=${c.id}`}>{contactName(c)}</Link>
          {i === 0 && lead.contacts.length > 1 && <small> primary</small>}
        </span>
      ))}
    </>
  );
}
